import type React from "react";
import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useAppDispatch from "../../hooks/useAppDispatch";
import {
    fetchNotifications,
    markNotificationRead,
    markAllRead,
    removeNotification,
} from "../../store/notificationSlice";
import type { RootState } from "../../store/store";
import { Check, X, Loader2 } from "lucide-react";
import { Button } from "../misc/Button";
import { FaBell } from "react-icons/fa";
import { useIsMobile } from "../../hooks/useIsMobile";
import NotificationOverlayItem from "./NotificationOverlayItem";

const NotificationBell = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { list, unreadCount, loading } = useSelector(
        (state: RootState) => state.notifications
    );
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const isMobile = useIsMobile(900);

    const toggle = () => {
        if (isMobile) {
            navigate("/notifications");
            return;
        }

        setOpen((prev) => !prev);
        // Fetch latest notifications when opening
        if (!open) {
            dispatch(fetchNotifications({ page: 1, limit: 5 }));
        }
    };

    const handleMarkAllRead = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(markAllRead());
    };

    const handleViewAll = () => {
        navigate("/notifications");
        setOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={toggle}
                className="relative p-2 text-primary hover:bg-[#F0DFAF] rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-secondary"
                aria-label="Notifications"
            >
                <FaBell className="w-5 h-5 text-secondary" />
                <AnimatePresence>
                    {unreadCount > 0 && (
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary rounded-full"
                        >
                            {unreadCount > 9 ? "9+" : unreadCount}
                        </motion.div>
                    )}
                </AnimatePresence>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{
                            opacity: 1,
                            x: isMobile ? 76 : 0,
                            y: 0,
                            scale: 1,
                        }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className={`absolute right-0 mt-2 ${
                            isMobile ? "w-screen" : "w-80"
                        } bg-white rounded-lg shadow-lg border border-gray-100 z-40`}
                    >
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <h3 className="text-lg font-semibold text-primary">
                                Notifications
                            </h3>
                            <div className="flex space-x-2">
                                {unreadCount > 0 && (
                                    <button
                                        onClick={handleMarkAllRead}
                                        className="flex items-center text-xs font-medium text-primary hover:text-secondary transition-colors"
                                    >
                                        <Check className="w-3.5 h-3.5 mr-1" />
                                        Mark all read
                                    </button>
                                )}
                                <button
                                    onClick={() => setOpen(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <div className="overflow-none">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <Loader2 className="w-8 h-8 text-secondary animate-spin mb-2" />
                                    <p className="text-sm text-gray-500">
                                        Loading notifications...
                                    </p>
                                </div>
                            ) : list.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                                    <div className="bg-[#F0DFAF] p-3 rounded-full mb-3">
                                        <FaBell className="w-6 h-6 text-secondary" />
                                    </div>
                                    <p className="text-gray-600 mb-1">
                                        No notifications yet
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        We'll notify you when something arrives
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {list.slice(0, 5).map((notification) => (
                                        <NotificationOverlayItem
                                            key={notification._id}
                                            notification={notification}
                                            onRead={() =>
                                                dispatch(
                                                    markNotificationRead(
                                                        notification._id
                                                    )
                                                )
                                            }
                                            onRemove={() =>
                                                dispatch(
                                                    removeNotification(
                                                        notification._id
                                                    )
                                                )
                                            }
                                            setOpen={setOpen}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="p-3 border-t border-gray-100">
                            <Button
                                onClick={handleViewAll}
                                className="w-full rounded"
                            >
                                View all notifications
                            </Button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NotificationBell;
